import { firebase } from 'src/firebase/';
import DatabaseAdapter from 'src/firebase/db/DatabaseAdapter';
import ValidationManager, { ValidationResponse } from 'src/firebase/db/ValidationManager';
import { getPrefectureName } from 'src/constants/PrefectureCode';
import { InternalError, ReferenceError } from 'src/firebase/db/ApplicationError';
import { ShopBagEntity, shopBagEntityKeys } from 'src/firebase/db/ShopBags';
import { addConsumptionTax } from 'src/helpers/MathHelper';

export type BusinessTimeInDay = {
  close_time: string;
  is_holiday: boolean;
  open_time: string;
};
export const initialBusinessTimeInDay: BusinessTimeInDay = {
  close_time: '',
  is_holiday: false,
  open_time: '',
};
export type BusinessTimeInDays = Record<
  | 'mon_in_business'
  | 'tue_in_business'
  | 'wed_in_business'
  | 'thu_in_business'
  | 'fri_in_business'
  | 'sat_in_business'
  | 'sun_in_business'
  | 'hol_in_business',
  BusinessTimeInDay
>;
export const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'hol'] as const;
export const initialBusinessTimeInDays: BusinessTimeInDays = days.reduce(
  (acc, d) => ({
    ...acc,
    [`${d}_in_business`]: initialBusinessTimeInDay,
  }),
  {} as BusinessTimeInDays,
);

type LatLng = {
  latitude: number;
  longitude: number;
};
const initialLatLng: LatLng = {
  latitude: 0,
  longitude: 0,
};

type shopOwnersEntity = {
  mail_senders?: string[];
};

export const shopOwnersInitialEntity: shopOwnersEntity = {
  mail_senders: [],
};

export const shopMenuInitialEntity = {
  id: '',
  name: '',
  image: '',
  price: 0,
  price_tax: 0,
  is_recommended: false,
  publish_status: '0',
  is_deleted: false,
};

export type ShopMenuEntity = typeof shopMenuInitialEntity;

export const shopInitialEntity = {
  address: '',
  address_number: '',
  baked_breads: [] as string[],
  city: '',
  created_at: {} as firebase.firestore.Timestamp,
  facebook_url: '',
  has_eatin: true,
  id: '',
  images: [] as string[],
  instagram_url: '',
  is_deleted: false,
  kana: '',
  latlng: initialLatLng,
  line_url: '',
  name: '',
  other_holiday: '',
  order: 0,
  phone: '',
  postal_code: '',
  prefecture_id: '',
  prefecture_name: '',
  publish_status: '0',
  released_time: undefined as Date | undefined,
  shop_url: '',
  tags: [] as string[],
  twitter_url: '',
  updated_at: {} as firebase.firestore.Timestamp,
  menus: [] as ShopMenuEntity[],
  bags: [] as ShopBagEntity[],
  note_distributor: '',
  note_contact: '',
  note_responsible_person: '',
  owners: shopOwnersInitialEntity,
  ...initialBusinessTimeInDays,
};
export type ShopEntity = typeof shopInitialEntity;
export const shopEntityKeys = (Object.keys(shopInitialEntity) as unknown) as (keyof ShopEntity)[];

export const shopMenuEntityKeys = (Object.keys(
  shopMenuInitialEntity,
) as unknown) as (keyof ShopMenuEntity)[];

class ShopsDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    this.setSchemaKeys(shopEntityKeys);
  }

  isRequired(key: string) {
    return [
      'name',
      'kana',
      'postal_code',
      'prefecture_id',
      'city',
      'address',
      'note_distributor',
      'note_contact',
      'note_responsible_person',
    ].includes(key);
  }

  isMenuRequired(key: string) {
    return ['name', 'image', 'price'].includes(key);
  }

  getValidationErrors(query: Partial<ShopEntity>): ValidationResponse | null {
    const validator = new ShopValidationManager();
    shopEntityKeys.forEach((key) => {
      if (this.isRequired(key)) {
        validator.isRequired(key, query[key]);
      }
      switch (key) {
        case 'mon_in_business':
        case 'tue_in_business':
        case 'wed_in_business':
        case 'thu_in_business':
        case 'fri_in_business':
        case 'sat_in_business':
        case 'sun_in_business':
        case 'hol_in_business':
          validator.isValidInBusiness(key, query[key]);
          break;
        case 'name': {
          const value = query[key];
          value && validator.validateMaxLength(key, value, 140);
          break;
        }
        case 'kana': {
          const value = query[key];
          value && validator.isKana(key, value);
          break;
        }
        case 'postal_code': {
          const value = query[key];
          value && validator.isPostalCode(value);
          break;
        }
        case 'phone': {
          const value = query[key];
          value && validator.isPhoneNumber(key, value);
          break;
        }
        case 'shop_url':
        case 'facebook_url':
        case 'line_url':
        case 'instagram_url':
        case 'twitter_url': {
          const value = query[key];
          value && validator.isUrl(key, value);
          break;
        }
        case 'has_eatin':
        case 'is_deleted':
          validator.isBoolean(key, query[key]);
          break;
      }
    });
    return validator.isPass() ? null : validator.throwValidateError(validator.getErrors());
  }

  getBagsValidationErrors(query: ShopBagEntity[]): ValidationResponse | null {
    const validator = new ShopValidationManager();
    const isRequired = ['name'];
    query.forEach((bag, index) => {
      shopBagEntityKeys.forEach((key) => {
        const vKey = `${key}_${index}`;
        if (isRequired.includes(key)) {
          validator.isRequired(vKey, bag[key]);
        }
        switch (key) {
          case 'price': {
            validator.isGtZero(vKey, bag[key]);
            break;
          }
        }
      });
    });

    return validator.isPass() ? null : validator.throwValidateError(validator.getErrors());
  }

  getMenuValidationErrors(
    query: Partial<ShopMenuEntity>,
    shopData: ShopEntity,
  ): ValidationResponse | null {
    const validator = new ValidationManager();

    const maxRecommendationCount = 3;
    let existRecoomendationCount = 0;
    shopData.menus.forEach((n) => {
      if (n.is_recommended && !n.is_deleted) existRecoomendationCount++;
    });
    console.log(existRecoomendationCount);

    shopMenuEntityKeys.forEach((key) => {
      if (this.isMenuRequired(key)) {
        validator.isRequired(key, query[key]);
      }
      switch (key) {
        case 'name': {
          const value = query[key];
          value && validator.validateMaxLength(key, value, 140);
          break;
        }
        case 'price':
          validator.isDigit(key, query[key]);
          break;
        case 'is_recommended':
          validator.isBoolean(key, query[key]);
          if (query[key] && existRecoomendationCount >= maxRecommendationCount) {
            validator.addErrors(key, 'お店のおすすめ商品は3個までにしてください');
          }
          break;
        case 'is_deleted':
          validator.isBoolean(key, query[key]);
          break;
      }
    });
    return validator.isPass() ? null : validator.throwValidateError(validator.getErrors());
  }

  async create(query: Partial<ShopEntity>) {
    const filteredQuery = this.filterQuery(query);
    const validationErrors = this.getValidationErrors(filteredQuery);
    if (validationErrors) return validationErrors;

    const requestQuery = {
      ...filteredQuery,
      prefecture_name: getPrefectureName(query.prefecture_id!),
      baked_breads: [],
      menus: [],
      released_time: query.released_time || null,
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
      images: await this.uploadImages(query.images!),
    };

    console.log('postQuery:shop create', requestQuery);

    const ref = this.getConnection();
    return ref
      .add(requestQuery)
      .then(async (snapshot) => {
        const id = snapshot.id;
        await ref.doc(snapshot.id).update({
          id: id,
        });
        return this.formatSuccess({
          id: id,
        });
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  async update(query: Partial<ShopEntity>) {
    const filteredQuery = this.filterQuery(query);

    const validationErrors = this.getValidationErrors(filteredQuery);
    if (validationErrors) return validationErrors;

    const requestQuery = {
      ...filteredQuery,
      prefecture_name: getPrefectureName(query.prefecture_id!),
      released_time: query.released_time || null,
      updated_at: this.getServerTimeStamp(),
      images: await this.uploadImages(query.images!),
    };
    console.log('postQuery:shop update', requestQuery);

    return this.getDocumentRef(filteredQuery.id!)
      .update(requestQuery)
      .then(async () => {
        const data = await this.getDocument(filteredQuery.id!);
        const formatted = this.formatShopData(data);
        return this.formatSuccess(formatted);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  async updateBags(id: string, query: ShopBagEntity[]) {
    const validationErrors = this.getBagsValidationErrors(query);
    if (validationErrors) return validationErrors;

    const shopRef = this.getDocumentRef(id);
    await shopRef.update({ updated_at: this.getServerTimeStamp() });

    const subCollection = this.getDocumentRef(id).collection('bags');
    const newRecords = query.filter((v) => !v.id);
    const edittedRecords = query.filter((v) => v.id);

    return await subCollection
      .get()
      .then(async (snapshot) => {
        console.log('HERE!!');
        //既存の全レコード
        const allRecords = await Promise.all(
          snapshot.docs.map(async (doc) => (await doc.data()) as ShopBagEntity),
        );

        // 削除対象のレコードを削除
        const deleteRecords = allRecords.filter((x) => !edittedRecords.find((y) => x.id === y.id));
        await Promise.all(
          deleteRecords.map(async (record) => {
            await subCollection.doc(record.id).delete();
          }),
        );

        // 更新対象のレコードを更新
        await Promise.all(
          edittedRecords.map(async (record) => {
            await subCollection.doc(record.id).update({
              ...record,
              updated_at: this.getServerTimeStamp(),
            });
          }),
        );

        // 新規作成のレコードを作成
        await Promise.all(
          newRecords.map(async (record) => {
            await subCollection.add(record).then((doc) => {
              doc.update({
                id: doc.id,
                updated_at: this.getServerTimeStamp(),
                created_at: this.getServerTimeStamp(),
              });
            });
          }),
        );

        return this.formatSuccess({ id });
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  async addBakedBread(document_id: string, ref: any) {
    return this.getDocumentRef(document_id).update({
      baked_breads: firebase.firestore.FieldValue.arrayUnion(ref),
    });
  }

  async removeBakedBread(document_id: string, ref: any) {
    return this.getDocumentRef(document_id).update({
      baked_breads: firebase.firestore.FieldValue.arrayRemove(ref),
    });
  }

  async updateMenus(document_id: string, menu_id: string, query: ShopMenuEntity) {
    const filteredQuery = this.filterQuery(query, shopMenuEntityKeys);
    const shopDocRef = this.getDocument(document_id);
    const shopData = await shopDocRef;

    const validationErrors = this.getMenuValidationErrors(filteredQuery, shopData);
    if (validationErrors) return validationErrors;
    const menus = shopData.menus;
    const updMenus = await Promise.all(
      menus.map(async (_: ShopMenuEntity) => {
        if (_.id === menu_id) {
          return {
            ...query,
            price_tax: addConsumptionTax(_.price, true),
            image: filteredQuery.image ? await this.uploadImage(filteredQuery.image) : undefined,
            updated_at: firebase.firestore.Timestamp.now(),
          };
        }
        return _;
      }),
    );
    console.log('updMenus');
    return await this.getDocumentRef(document_id)
      .update({
        menus: updMenus,
      })
      .then(async () => {
        const data = await this.getDocument(document_id);
        const formatted = this.formatShopData(data);
        return this.formatSuccess(formatted);
      })
      .catch((err) => {
        console.log('Error', err);
        throw new InternalError(err.message);
      });
  }

  async addMenus(document_id: string, query: ShopMenuEntity) {
    const filteredQuery = this.filterQuery(query, shopMenuEntityKeys);
    const shopDocRef = this.getDocument(document_id);
    const shopData = await shopDocRef;

    const validationErrors = this.getMenuValidationErrors(filteredQuery, shopData);
    if (validationErrors) return validationErrors;

    return await this.getDocumentRef(document_id)
      .update({
        menus: firebase.firestore.FieldValue.arrayUnion({
          ...query,
          image: filteredQuery.image ? await this.uploadImage(filteredQuery.image) : undefined,
          updated_at: firebase.firestore.Timestamp.now(),
        }),
      })
      .then(async () => {
        const data = await this.getDocument(document_id);
        const formatted = this.formatShopData(data);
        return this.formatSuccess(formatted);
      })
      .catch((err) => {
        console.log('Error', err);
        throw new InternalError(err.message);
      });
  }

  getShops() {
    const shops: ShopEntity[] = [];
    return (
      this.getConnection()
        .where('is_deleted', '==', false)
        // .orderBy("created_at", "desc")
        .get()
        .then((snapshot) => {
          if (snapshot.empty) {
            return this.throwError(44, '表示する店舗が見つかりません');
          }
          snapshot.forEach((doc) => {
            const data = doc.data() as ShopEntity;
            const formatted = this.formatShopData(data);
            shops.push(formatted);
          });
          return this.formatSuccess(shops);
        })
        .catch((err) => {
          console.log(err);
          return this.throwError(500, 'System Error');
        })
    );
  }

  async getShop(document_id: string) {
    const shopRef = this.getDocumentRef(document_id);
    return shopRef
      .get()
      .then(async (doc) => {
        if (!doc.exists) {
          throw new ReferenceError('対象店舗が存在しません');
        }
        const shop = (await doc.data()) as ShopEntity;

        // レジ袋を取得
        const bagsQuerySnapShot = await shopRef.collection('bags').get();
        const bags: ShopBagEntity[] = await Promise.all(
          bagsQuerySnapShot.docs.map(async (doc) => {
            const data = (await doc.data()) as ShopBagEntity;
            return {
              ...data,
              id: doc.id,
            };
          }),
        );
        const formatted = await this.formatShopData({
          ...shop,
          bags,
        });
        return this.formatSuccess(formatted);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async getAddress(zipcode: string) {}

  /**
   * お店の配信先メールアドレスを更新する
   */
  updateOwnersMailSenders(document_id: string, method: 'add' | 'delete', address: string) {
    const validator = new ValidationManager();
    validator.isAddress('mail_address', address);
    if (!validator.isPass()) {
      return validator.throwValidateError(validator.getErrors());
    }

    const updateArray = (method: 'add' | 'delete', address: string) => {
      if (method === 'add') {
        return firebase.firestore.FieldValue.arrayUnion(address);
      } else {
        return firebase.firestore.FieldValue.arrayRemove(address);
      }
    };

    const doc = this.getDocumentRef(document_id);
    return doc
      .update({
        'owners.mail_senders': updateArray(method, address),
      })
      .then(async () => {
        const data = await this.getDocument(document_id);
        const formatted = this.formatShopData(data);
        return this.formatSuccess(formatted);
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  formatShopData(shop: ShopEntity) {
    const menus = shop.menus
      .filter((_) => {
        return !_.is_deleted;
      })
      .map((_) => {
        const calcMenuPrice = addConsumptionTax(_.price, true);
        return {
          ..._,
          price_tax: calcMenuPrice,
        };
      });
    return {
      ...shop,
      menus: menus,
    };
  }
}

class ShopValidationManager extends ValidationManager {
  isPostalCode(v: string) {
    const is = this.regexpTest('^[0-9]{7}$', v);
    if (!is) this.addErrors('postal_code', '郵便番号は7桁の半角数字で入力してください');
  }

  isValidInBusiness(key: keyof BusinessTimeInDays, payload?: Partial<BusinessTimeInDay>): void {
    const day = getDayLabelJp((key.replace('_in_business', '') as unknown) as typeof days[number]);
    if (!payload || (!payload.is_holiday && (!payload.open_time || !payload.close_time))) {
      this.addErrors(`in_business`, `${day}に営業時間もしくは定休日が設定されていません。`);
      return;
    }
    this.isBoolean('in_business', payload.is_holiday);

    if (!payload.is_holiday && !this.isValidOpenTime(payload.open_time!, payload.close_time!)) {
      this.addErrors(`in_business`, `${day}の営業時間が不正です`);
    }
  }
}

export default new ShopsDB({
  schema: 'shops',
});

export const getDayLabelJp = (label: typeof days[number]) => {
  switch (label) {
    case 'mon':
      return '月曜日';
    case 'tue':
      return '火曜日';
    case 'wed':
      return '水曜日';
    case 'thu':
      return '木曜日';
    case 'fri':
      return '金曜日';
    case 'sat':
      return '土曜日';
    case 'sun':
      return '日曜日';
    case 'hol':
      return '祝日';
  }
};
